/**
 * Route Card Component - Compact Version
 * Individual route summary card with driver assignment/removal
 */

import { useState, useRef, useEffect } from 'react';
import { Card } from '../ui';
import { User, MapPin, Clock, UserPlus, Loader2, Trash2, UserMinus, Eye, EyeOff, Navigation } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { Route } from '../../types';
import { useCan } from '../../contexts/useAuth';

interface Driver {
  driver_uid: string;
  full_name: string;
}

// Route colors matching the map
const ROUTE_COLORS = [
  '#3B82F6', // Blue
  '#2563EB', // Blue-600
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#84CC16', // Lime
  '#F97316', // Orange
  '#6366F1', // Indigo
];

/**
 * A stable ROUTE_COLORS index for a zone group.
 *
 * Group routes carry no zone_color, and every route of one group must share a
 * swatch — so the colour is derived from the code itself rather than from the
 * route's position in the list, which changes as routes are filtered.
 */
function groupColorIndex(groupCode: string): number {
  let hash = 0;
  for (let i = 0; i < groupCode.length; i++) hash = (hash * 31 + groupCode.charCodeAt(i)) >>> 0;
  return hash % ROUTE_COLORS.length;
}

interface RouteCardProps {
  route: Route;
  routeIndex?: number;
  isSelected?: boolean;
  isVisible?: boolean;
  onClick?: () => void;
  onViewDetails?: () => void;
  onToggleVisibility?: (routeId: string) => void;
  drivers?: Driver[];
  onAssignDriver?: (routeId: string, driverUid: string) => void | Promise<void>;
  onRemoveDriver?: (routeId: string) => void | Promise<void>;
  onDeleteRoute?: (routeId: string) => void | Promise<void>;
  isDeleting?: boolean;
  progress?: {
    completed: number;
    total: number;
    hasWarning: boolean;
  };
  /** Non-cancelled stop count (matches the detail panel). Falls back to route.stop_count when undefined. */
  activeStopCount?: number;
}

export default function RouteCard({
  route,
  routeIndex = 0,
  isSelected,
  isVisible = true,
  onClick,
  onToggleVisibility,
  drivers = [],
  onAssignDriver,
  onRemoveDriver,
  onDeleteRoute,
  isDeleting = false,
  progress,
  activeStopCount,
}: RouteCardProps) {
  const can = useCan();
  // Ops scan this list by zone, so the zone's own colour has to win — otherwise two
  // routes of the same zone get different swatches and a Zone C route can share a
  // colour with a Zone D one. Only fall back for routes with no zone.
  //
  // A SHIFT1 GROUP route has no zone and therefore no zone_color, which would put
  // it back on the per-index fallback and give two routes of the SAME group
  // different swatches — the very thing above. Derive a stable colour from the
  // group code instead so a group scans as one thing.
  const zoneColor =
    route.zone_color ||
    (route.group_code ? ROUTE_COLORS[groupColorIndex(route.group_code)] : null) ||
    route.route_color ||
    ROUTE_COLORS[routeIndex % ROUTE_COLORS.length];

  // A zone needing more than one vehicle yields "Zone C" and "Zone C 2". Pull that
  // trailing index out so it can be shown as a chip rather than a second name that
  // reads like a different zone.
  const splitIndex =
    route.zone_name && route.route_name !== route.zone_name
      ? route.route_name.slice(route.zone_name.length).trim() || null
      : null;
  const [isAssigning, setIsAssigning] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showDriverSelect, setShowDriverSelect] = useState(false);
  const selectRef = useRef<HTMLSelectElement>(null);

  // Close driver select when clicking outside
  useEffect(() => {
    if (!showDriverSelect) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setShowDriverSelect(false);
      }
    };

    // Add listener with a small delay to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDriverSelect]);

  // Check if route actually has a driver assigned (from database, not operation state)
  const isUnassigned = !route.driver_uid || route.driver_uid === '';
  const assignedDriverName =
    (typeof route.driver_name === 'string' && route.driver_name.trim())
      ? route.driver_name
      : drivers.find((d) => d.driver_uid === route.driver_uid)?.full_name;
  
  // Check if route is completed - hide management actions if so
  const isCompleted = route.status === 'completed';

  const handleAssignDriver = async (driverUid: string) => {
    if (!onAssignDriver) return;
    setIsAssigning(true);
    try {
      await onAssignDriver(route._id, driverUid);
      setShowDriverSelect(false);
    } catch (error) {
      console.error('Failed to assign driver:', error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveDriver = async () => {
    if (!onRemoveDriver) return;
    setIsRemoving(true);
    try {
      await onRemoveDriver(route._id);
    } catch (error) {
      console.error('Failed to remove driver:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <Card
      padding="sm"
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-2 ring-blue-500 bg-blue-50'
      )}
      onClick={onClick}
    >
      {/* Header Row - Route Name & Status */}
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {/* Zone colour, not the per-index route colour — ops scan by zone, so every
              route of a zone must carry the SAME swatch. Falls back to the old
              behaviour for routes that have no zone. */}
          <div
            className="w-2.5 h-6 rounded-full flex-shrink-0 ring-1 ring-black/10"
            style={{ backgroundColor: zoneColor }}
            title={
              route.group_code
                ? `Zone group: ${route.zone_name} — covers several zones`
                : route.zone_name
                  ? `Zone: ${route.zone_name}`
                  : route.route_name
            }
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <p dir="auto" className="font-semibold text-sm text-gray-900 break-words min-w-0">
                {route.zone_name || route.route_name}
              </p>
              {/* A group route crosses several zones, so the driver is not
                  working one area. Say so rather than letting "Karkh" read as
                  just another zone name. */}
              {route.group_code && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 border border-indigo-300 bg-indigo-50 text-indigo-700"
                  title={`Zone group ${route.group_code} — this route covers several zones`}
                >
                  GROUP
                </span>
              )}
              {/* A zone that needed more than one vehicle produces "Zone C", "Zone C 2".
                  Show that as an explicit chip instead of a second name that reads
                  like a different zone. */}
              {splitIndex && (
                <span
                  className="px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 border"
                  style={{ color: zoneColor, borderColor: zoneColor, backgroundColor: `${zoneColor}14` }}
                  title={`Route ${splitIndex} of this zone — the zone needed more than one vehicle`}
                >
                  #{splitIndex}
                </span>
              )}
            </div>
          </div>
          {/* Generated runs carry their scope: SHIFT1/SHIFT2, or COLD for the
              temperature-control pass. Without COLD here the cold van's route
              showed no badge at all and read as hand-built. */}
          {(route.run_slot?.startsWith('SHIFT') || route.run_slot === 'COLD') && (
            <span
              className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 flex-shrink-0"
              title={`Generated for ${route.run_slot}`}
            >
              {route.run_slot}
            </span>
          )}
        </div>
        {/* Status Badge and Visibility Toggle */}
        <div className="flex items-center gap-1.5">
          <div className={cn(
            "px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0",
            (route.status === 'draft' || (route.status === 'published' && isUnassigned)) && "bg-gray-100 text-gray-700",
            (route.status === 'published' && !isUnassigned) && "bg-blue-100 text-blue-700",
            route.status === 'in_progress' && "bg-yellow-100 text-yellow-700",
            route.status === 'completed' && "bg-green-100 text-green-700",
            route.status === 'cancelled' && "bg-red-100 text-red-700"
          )}>
            {(route.status === 'draft' || (route.status === 'published' && isUnassigned)) && 'New'}
            {(route.status === 'published' && !isUnassigned) && 'Assigned'}
            {route.status === 'in_progress' && 'In Progress'}
            {route.status === 'completed' && 'Completed'}
            {route.status === 'cancelled' && 'Cancelled'}
          </div>
          {/* Toggle Visibility Button */}
          {onToggleVisibility && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleVisibility(route._id);
              }}
              className={cn(
                "p-1 rounded transition-colors",
                isVisible 
                  ? "text-blue-500 hover:bg-blue-50" 
                  : "text-gray-400 hover:bg-gray-100"
              )}
              title={isVisible ? "Hide from map" : "Show on map"}
            >
              {isVisible ? (
                <Eye className="h-3.5 w-3.5" />
              ) : (
                <EyeOff className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Driver Row */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className={cn(
          "p-1 rounded flex-shrink-0",
          isUnassigned ? "bg-amber-100" : "bg-green-100"
        )}>
          {isUnassigned ? (
            <UserPlus className="h-3.5 w-3.5 text-amber-600" />
          ) : (
            <User className="h-3.5 w-3.5 text-green-600" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p dir="auto" className={cn(
            "text-xs break-words",
            isUnassigned ? "text-amber-700 font-medium" : "text-gray-600"
          )}>
            {isUnassigned ? 'Unassigned' : (assignedDriverName || 'Assigned')}
          </p>
        </div>
      </div>

      {/* Progress Bar - Show if route has progress data */}
      {progress && progress.total > 0 && (() => {
        // Denominator excludes cancelled stops (matches the stop-count stat and
        // the detail panel), so a route with all serviceable stops done reads 100%.
        const progressTotal = activeStopCount && activeStopCount > 0 ? activeStopCount : progress.total;
        const progressPct = Math.min(100, (progress.completed / progressTotal) * 100);
        return (
          <div className="mb-1.5">
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="text-gray-600">
                {progress.completed}/{progressTotal} stops done
              </span>
              {progress.hasWarning && (
                <span className="text-orange-600 font-medium">⚠️ Issues</span>
              )}
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1">
              <div
                className={cn(
                  "h-1 rounded-full transition-all",
                  progress.hasWarning ? "bg-orange-500" : "bg-green-500"
                )}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        );
      })()}

      {/* Compact Stats Row */}
      <div className="flex items-center gap-2.5 text-xs text-gray-500 mb-1.5">
        <span className="flex items-center gap-1">
          <MapPin className="h-3 w-3" />
          {activeStopCount ?? route.stop_count}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {Math.round(route.google_eta_total_min || route.total_duration_min)}m
        </span>
        <span>{route.total_distance_km.toFixed(1)}km</span>
        {route.start_location_mode === 'hub_start' && (
          <span className="flex items-center gap-1 text-blue-600" title="Starts from warehouse">
            <Navigation className="h-3 w-3" />
          </span>
        )}
        {/*
          Orders against the ceiling the append sweep actually enforces, so a
          manager can see a route filling up before it starts refusing work.
        */}
        {route.capacity && (
          <span
            className={
              route.capacity.status === 'full'
                ? 'ml-auto font-medium text-red-600'
                : route.capacity.status === 'near_full'
                  ? 'ml-auto font-medium text-amber-600'
                  : 'ml-auto text-gray-500'
            }
            title={
              `${route.capacity.load} of ${route.capacity.capacity} orders — ` +
              (route.capacity.headroom > 0
                ? `room for ${route.capacity.headroom} more`
                : 'no room left, new pickups will not be added') +
              // A route "full" below the platform rule is a stale driver schedule,
              // not a full route — and it cancels the append headroom the 80% fill
              // left. Both numbers come from the API so a raise needs no edit here.
              (route.capacity.capped_below_platform
                ? `. This driver's schedule caps the route at ${route.capacity.capacity}, below the ${route.capacity.platform_capacity}-order platform limit.`
                : '') +
              (route.capacity.capacity_source === 'default'
                ? '. Driver has no active schedule, so this uses the platform default.'
                : '')
            }
          >
            {route.capacity.load}/{route.capacity.capacity}
            {route.capacity.status === 'full'
              ? ' full'
              : route.capacity.status === 'near_full'
                ? ' near full'
                : ''}
            {route.capacity.capped_below_platform && (
              <span className="ml-0.5 text-amber-600" title="Schedule caps below the platform limit">
                *
              </span>
            )}
          </span>
        )}
      </div>
      {/* A filling route is the thing a manager wants to catch early, so it gets a bar. */}
      {route.capacity && route.capacity.status !== 'light' && (
        <div className="mb-1.5 h-1 w-full overflow-hidden rounded bg-gray-100">
          <div
            className={
              route.capacity.status === 'full'
                ? 'h-full bg-red-500'
                : route.capacity.status === 'near_full'
                  ? 'h-full bg-amber-500'
                  : 'h-full bg-sky-400'
            }
            style={{ width: `${route.capacity.fill_percent}%` }}
          />
        </div>
      )}

      {route.core_split_lists && route.core_split_lists.length > 1 && (
        <p role="alert" className="mb-1.5 rounded bg-red-50 px-2 py-1 text-xs text-red-700">
          Core put this driver&apos;s tasks on {route.core_split_lists.length} lists (
          {route.core_split_lists.map((l) => l.open_parents).join(' + ')}). The driver may see only one. Fix: Unassign
          driver, then Assign driver again.
        </p>
      )}

      {/* Action Buttons Row - hide if route is completed */}
      {!isCompleted && onAssignDriver && drivers.length > 0 && (
        <div className="flex items-center gap-1">
          {showDriverSelect ? (
            <select
              ref={selectRef}
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
              onChange={(e) => {
                if (e.target.value) handleAssignDriver(e.target.value);
              }}
              disabled={isAssigning || !can('routes.edit')}
              defaultValue=""
              onClick={(e) => e.stopPropagation()}
            >
              <option value="">Select driver...</option>
              {drivers
                .filter((driver) => driver.driver_uid !== route.driver_uid)
                .map((driver) => (
                  <option key={driver.driver_uid} value={driver.driver_uid}>
                    {driver.full_name}
                  </option>
                ))}
            </select>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowDriverSelect(true);
              }}
              className={cn(
                "flex-1 px-2 py-1 text-xs rounded flex items-center justify-center gap-1 transition-colors",
                isUnassigned
                  ? "text-amber-700 bg-amber-50 hover:bg-amber-100"
                  : "text-blue-700 bg-blue-50 hover:bg-blue-100"
              )}
            >
              <UserPlus className="h-3 w-3" />
              {isUnassigned ? 'Assign' : 'Change'}
            </button>
          )}

          {/* Remove Driver Button */}
          {!isUnassigned && onRemoveDriver && can('routes.edit') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveDriver();
              }}
              disabled={isRemoving}
              className="p-1 text-orange-500 hover:bg-orange-50 rounded transition-colors"
              title="Remove driver"
            >
              {isRemoving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <UserMinus className="h-3.5 w-3.5" />
              )}
            </button>
          )}

          {/* Delete Route Button - always show if handler provided */}
          {onDeleteRoute && can('routes.generate') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRoute(route._id);
              }}
              disabled={isDeleting}
              className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
              title="Delete route"
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
